import { toDateValue } from '@/api/customFields/formulaWireUnits';

// A formula answers with a string. Each control reads a different shape — a
// Yes/No field holds Y or N, a date widget holds epoch milliseconds — so the
// result has to be turned into what the control takes before it can be shown
// in it. Where it cannot be, the field says so rather than showing a value it
// could never save.
export type ControlValue =
  | { value: unknown; unstorable?: never }
  | { value?: never; unstorable: string };

// What a short text column holds. Long text and rich text are their own
// columns and take far more, so the cap is the short one's alone.
const SHORT_TEXT_MAX = 255;

// The same shape the settings drawer refuses a default by, so both ends of the
// field agree on what counts as a link.
const URL_PATTERN =
  /^(https?:\/\/)?([\w-]+(\.[\w-]+)*\.[\w-]{2,})(:\d+)?(\/\S*)?$/;
const TEXT_LIMITED_KINDS = ['string'];

// The two the Yes/No control binds to; the labels beside them are the field's.
const YES_NO = ['Y', 'N'];

const quoted = (value: string) => `“${value}”`;

// The options a select binds to, as the field config builds them: the control
// holds `value` (the tag id) and shows `label`.
export interface ControlOption {
  label: string;
  value: string | number;
}

// A select's control holds tag ids, while the formula answers with the option
// names it created — so the answer has to be resolved against the field's own
// options. Matching the id too costs nothing and means the shape the server
// sends cannot silently empty the dropdown.
const toOptionValue = (name: string, options: ControlOption[]) =>
  options.find(
    (option) =>
      String(option.value) === name ||
      option.label.trim().toLowerCase() === name.toLowerCase()
  )?.value;

// A multi answer crosses as one comma-separated string — the language has no
// list literal — and is all-or-nothing on the server: one unmatched name
// leaves the field uncomputed rather than setting fewer options than asked for.
const toOptionValues = (text: string, options: ControlOption[]) => {
  const names = text
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  const ids = names.map((name) => toOptionValue(name, options));
  return ids.every((id) => id !== undefined)
    ? (ids as (string | number)[])
    : null;
};

export const toControlValue = (
  kind: string | undefined,
  computed: string | number | null | undefined,
  // A select resolves its answer against these; every other kind ignores them.
  {
    options = [],
    multi = false,
  }: { options?: ControlOption[]; multi?: boolean } = {}
): ControlValue => {
  if (computed === null || computed === undefined || computed === '')
    return { value: '' };

  const text = String(computed);

  if (kind === 'enum_boolean')
    return YES_NO.includes(text)
      ? { value: text }
      : {
          unstorable: `A Yes/No field can only hold Y or N, but the formula can produce ${quoted(
            text
          )}.`,
        };

  if (kind === 'rel_tag') {
    if (multi) {
      const ids = toOptionValues(text, options);
      return ids
        ? { value: ids }
        : {
            unstorable: `${quoted(
              text
            )} doesn\u2019t match any of this field\u2019s options. Ask your admin to fix the formula.`,
          };
    }
    const id = toOptionValue(text, options);
    return id === undefined
      ? {
          unstorable: `${quoted(
            text
          )} isn\u2019t one of this field\u2019s options. Ask your admin to fix the formula.`,
        }
      : { value: id };
  }

  if (kind === 'date') {
    // dayjs answers NaN for anything it cannot read; 0 is a real date, so the
    // check is finiteness rather than truthiness.
    // Not `text`: an epoch sent as a number would be re-read as a year.
    const epoch = toDateValue(computed);
    return Number.isFinite(epoch)
      ? { value: epoch }
      : {
          unstorable: `${quoted(
            text
          )} isn\u2019t a valid date for this field. Ask your admin to fix the formula.`,
        };
  }

  // A url column only takes a link, and the save refuses anything else — the
  // same treatment as an over-long result, which is what Venil asked for on
  // CORE-5477. Showing the value would be one the offer could never keep.
  if (kind === 'url' && !URL_PATTERN.test(text))
    return {
      unstorable: `The calculated value ${quoted(
        text
      )} isn\u2019t a valid link for this field. Ask your admin to fix the formula.`,
    };

  // A short text column holds 255 characters, and the save refuses a longer
  // one — so showing it would be a value the offer could never keep. Said
  // here rather than left to the save, which is on a field the filler often
  // cannot edit (CORE-5490, Venil and Daniel's level 2).
  if (TEXT_LIMITED_KINDS.includes(kind ?? '') && text.length > SHORT_TEXT_MAX)
    return {
      unstorable: `The calculated value is ${text.length} characters, but this field holds ${SHORT_TEXT_MAX}. Ask your admin to fix the formula.`,
    };

  // Number, currency, url and rich text all take the answer as it comes.
  return { value: text };
};
